/**
 * Date formatting utilities for display
 * Uses date-fns for consistency across the application
 */

import {
  format,
  addDays,
  startOfWeek as dateFnsStartOfWeek,
  endOfWeek as dateFnsEndOfWeek,
  isSameWeek,
} from "date-fns";

/**
 * Day and month names for display
 * These match the existing constants used in the app
 */
export const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Get the abbreviated day name for a date
 * @returns e.g. "Mon", "Tue", "Wed"
 */
export const getDayName = (date) => {
  return DAY_NAMES[date.getDay()];
};

/**
 * Format date for display in shift headers
 * @returns e.g. "Mon Sep 15"
 */
export const formatShiftDate = (date) => {
  return format(date, "EEE, MMM d");
};

/**
 * Get formatted next day information for overnight shifts
 * Returns the day name, month name, and day number for the next day
 */
export const formatNextDayInfo = (shiftDate) => {
  const nextDay = addDays(shiftDate, 1);
  return {
    dayName: DAY_NAMES[nextDay.getDay()],
    month: MONTH_NAMES[nextDay.getMonth()],
    day: nextDay.getDate(),
    date: nextDay,
  };
};

/**
 * Format a full date with day name
 * @returns e.g. "Monday, September 15, 2024"
 */
export const formatFullDate = (date) => {
  return format(date, "EEEE, MMMM d, yyyy");
};

/**
 * Format date range
 * @returns e.g. "Sep 15 - Sep 21"
 */
export const formatDateRange = (startDate, endDate) => {
  const startMonth = format(startDate, "MMM");
  const endMonth = format(endDate, "MMM");
  const startDay = format(startDate, "d");
  const endDay = format(endDate, "d");
  if (startMonth === endMonth) {
    return `${startMonth} ${startDay} - ${endDay}`;
  }
  return `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
};

/**
 * Get the start of the week (Monday) for any date
 */
export const getStartOfWeek = (date) => {
  // date-fns startOfWeek with weekStartsOn: 1 = Monday
  return dateFnsStartOfWeek(date, {
    weekStartsOn: 1,
  });
};

/**
 * Get the end of the week (Sunday) for any date
 */
export const getEndOfWeek = (date) => {
  // date-fns endOfWeek with weekStartsOn: 1 = Monday (making Sunday the end)
  return dateFnsEndOfWeek(date, {
    weekStartsOn: 1,
  });
};

/**
 * Check if a date is in the current week
 */
export const isCurrentWeek = (date) => {
  const today = new Date();
  return isSameWeek(date, today, {
    weekStartsOn: 1,
  });
};

/**
 * Get the Monday of the current week
 * Useful for initializing week views
 */
export const getCurrentWeekMonday = () => {
  return getStartOfWeek(new Date());
};

/**
 * Get all 7 dates for a week starting from Monday
 * @param weekStartDate - Any date in the week (will find the Monday)
 * @returns Array of 7 dates from Monday to Sunday
 */
export const getWeekDates = (weekStartDate) => {
  const monday = getStartOfWeek(weekStartDate);
  const dates = [];
  for (let i = 0; i < 7; i += 1) {
    dates.push(addDays(monday, i));
  }
  return dates;
};

/**
 * Format date for API calls (YYYY-MM-DD)
 */
export const formatDateForApi = (date) => {
  return format(date, "yyyy-MM-dd");
};

/**
 * Format date for display (e.g., "Jan 15, 2024")
 */
export const formatDateForDisplay = (date) => {
  return format(date, "MMM d, yyyy");
};

/**
 * Format date in short format without year (e.g., "Sep 15")
 */
export const formatShortDate = (date) => {
  return format(date, "MMM d");
};

/**
 * Helper function to parse date strings as local dates (not UTC)
 *
 * JavaScript's `new Date("2024-01-15")` creates a date at UTC midnight,
 * which can appear as the previous day in timezones behind UTC.
 * This function ensures the date is parsed as local midnight instead.
 *
 * @param dateStr - Date string in format "YYYY-MM-DD" or Date object
 * @returns Date object at local midnight
 */
export const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;

  // If already a Date object, return a copy
  if (dateStr instanceof Date) {
    return new Date(dateStr);
  }

  // Parse ISO date string as local date
  const parts = dateStr.split("-");
  if (parts.length !== 3) {
    // If not in expected format, fallback to standard parsing
    // This handles timestamps like "2024-01-15T10:00:00Z"
    return new Date(dateStr);
  }
  const [year, month, day] = parts.map(Number);
  // Note: Month is 0-indexed in JavaScript Date constructor
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

/**
 * Checks if a string is an ISO date string (YYYY-MM-DD format)
 */
export const isISODateString = (value) => {
  if (typeof value !== "string") return false;
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
};

/**
 * Format week range for DatePicker week display
 * @param firstDay - First day of the week
 * @param lastDay - Last day of the week
 * @returns Formatted string like "1/15-1/21"
 */
export const weekFormatter = (firstDay, lastDay) => {
  const startMonthNum = firstDay.getMonth() + 1;
  const endMonthNum = lastDay.getMonth() + 1;
  const startDayNum = firstDay.getDate();
  const endDayNum = lastDay.getDate();
  return `${startMonthNum}/${startDayNum}-${endMonthNum}/${endDayNum}`;
};

/**
 * Check if a date is a weekday (Monday-Friday)
 * @param date - Date to check
 * @returns true if the date is Monday through Friday
 */
export const isWeekday = (date) => {
  const day = date.getDay();
  return day >= 1 && day <= 5; // Monday = 1, Friday = 5
};
